import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import TimelineItem from "./TimelineItem";
import { timelineData, categories } from "../data/timeline";
import toast, { Toaster } from "react-hot-toast";
import headImage from "../assets/logo192.png"
const TimelineContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 2rem;
`;

const ProfileImage = styled(motion.div)`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: var(--gradient);
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  box-shadow: var(--shadow-lg);
  position: relative;
  overflow: hidden;
  padding: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    font-size: 2.5rem;
  }
`;

const NameContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.25rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Name = styled(motion.h1)`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  gap: 0.75rem;

  a,
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--secondary-bg);
    color: var(--text-secondary);
    text-decoration: none;
    font-size: 1rem;
    box-shadow: var(--shadow);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-lg);
    }

    &.github:hover {
      color: #333;
    }

    &.resume:hover {
      color: #0066cc;
    }

    &.twitter:hover {
      color: #000;
    }

    &.email:hover {
      color: #ea4335;
    }
  }

  @media (max-width: 768px) {
    gap: 0.5rem;

    a,
    button {
      width: 32px;
      height: 32px;
      font-size: 0.9rem;
    }
  }
`;

const Title = styled(motion.h2)`
  font-size: 1.2rem;
  font-weight: 500;
  background: var(--gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.75rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const TechStack = styled(motion.div)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0 auto 0.75rem;
  max-width: 800px;

  .tech {
    background: var(--accent-light);
    color: var(--accent);
    padding: 0.2rem 0.6rem;
    border-radius: 15px;
    font-size: 0.7rem;
    font-weight: 500;
    border: 1px solid var(--accent);
  }

  .more {
    background: var(--secondary-bg);
    color: var(--text-secondary);
    padding: 0.2rem 0.6rem;
    border-radius: 15px;
    font-size: 0.7rem;
    font-weight: 500;
    border: 1px solid var(--border);
  }

  @media (max-width: 768px) {
    gap: 0.375rem;
    max-width: 100%;

    .tech,
    .more {
      font-size: 0.75rem;
      padding: 0.2rem 0.6rem;
    }
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto 2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FilterContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterButton = styled(motion.button)`
  padding: 0.5rem 1rem;
  border-radius: 25px;
  border: 2px solid ${(props) => (props.active ? props.color : "transparent")};
  background: ${(props) =>
    props.active ? props.bgColor : "var(--secondary-bg)"};
  color: ${(props) => (props.active ? props.color : "var(--text-secondary)")};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;

  &:hover {
    background: ${(props) => props.bgColor};
    color: ${(props) => props.color};
    border-color: ${(props) => props.color};
  }
`;

const TimelineLine = styled(motion.div)`
  position: absolute;
  left: 50%;
  top: ${props => props.accordionOpen ? '600px' : '450px'};
  bottom: 50px;
  width: 4px;
  background: linear-gradient(to bottom, var(--accent), transparent);
  border-radius: 2px;
  z-index: 1;
  transition: top 0.3s ease;

  @media (max-width: 768px) {
    left: 30px;
    width: 2px;
    top: ${props => props.accordionOpen ? '800px' : '600px'};
  }
`;

const AccordionContainer = styled(motion.div)`
  margin-top: 2rem;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const BioSection = styled(motion.div)`
  margin-top: 2rem;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const BioContent = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 1rem;
  margin: 0;

  .highlight {
    color: var(--accent);
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const AccordionItem = styled(motion.div)`
  background: var(--secondary-bg);
  border-radius: 16px;
  margin-bottom: 1rem;
  overflow: hidden;
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
`;

const AccordionHeader = styled(motion.button)`
  width: 100%;
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  text-align: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  transition: all 0.3s ease;

  &:hover {
    background: var(--border);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 1.25rem 1.5rem;
  }
`;

const AccordionContent = styled(motion.div)`
  padding: 0 1.5rem 1.5rem;
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 1rem;
  text-align: left;

  .highlight {
    color: var(--accent);
    font-weight: 600;
  }

  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.75rem;
    line-height: 1.6;
  }

  p {
    margin-bottom: 1rem;
  }

  @media (max-width: 768px) {
    padding: 0 1.5rem 1.5rem;
    font-size: 0.95rem;

    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const FloatingElement = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  background: var(--gradient);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1000;
  box-shadow: var(--shadow-lg);

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
  }
`;

const Timeline = () => {
  const [filter, setFilter] = useState("All");
  const [filteredData, setFilteredData] = useState(timelineData);
  const [showSurprise, setShowSurprise] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);

  useEffect(() => {
    if (filter === "All") {
      setFilteredData(timelineData);
    } else {
      setFilteredData(timelineData.filter((item) => item.category === filter));
    }
  }, [filter]);

  const handleEmailClick = () => {
    navigator.clipboard.writeText("ymtibebu@gmail.com").then(() => {
      setEmailCopied(true);
      toast.success("Email copied to clipboard!", {
        duration: 2000,
        style: {
          background: "var(--secondary-bg)",
          color: "var(--text-primary)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          fontSize: "0.9rem",
        },
      });
      setTimeout(() => setEmailCopied(false), 3000);
    });
  };

  const handleSurprise = () => {
    const surprises = [
      "🎉 You found the secret button!",
      "✨ Thanks for exploring!",
      "🚀 Keep being awesome!",
      "🎯 Attention to detail pays off!",
      "💎 You're a curious one!",
    ];

    const randomSurprise =
      surprises[Math.floor(Math.random() * surprises.length)];
    toast.success(randomSurprise, {
      duration: 3000,
      style: {
        background: "var(--secondary-bg)",
        color: "var(--text-primary)",
        border: "1px solid var(--border)",
        borderRadius: "12px",
        fontSize: "0.9rem",
      },
    });

    setShowSurprise(true);
    setTimeout(() => setShowSurprise(false), 2000);
  };

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const headerVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const lineVariants = {
    hidden: { scaleY: 0, originY: 0 },
    visible: {
      scaleY: 1,
      transition: { duration: 1.5, ease: "easeOut", delay: 0.5 },
    },
  };

  const accordionData = [
    {
      title: "My mission in life 🎯",
      content: (
        <div>
          <p>
            I love dogs. My mission is to end dog euthanasia in USA using
            virtual pet gaming app in the future.
          </p>
          <ul>
            <li>
              <span className="highlight">Outside of work</span> - I volunteer
              at the Pueblo Animal Shelter in Colorado
            </li>
            <li>
              <span className="highlight">Favorite music</span> - 2010s indie
              rock is undefeated imo. I grew up in this era.
            </li>
            <li>
              <span className="highlight">Favorite sport</span> - Basketball
            </li>
            <li>
              <span className="highlight">Favorite place</span> - Pacific North
              West / Washington / Oregon - love that area
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "What about people skills 🚀",
      content: (
        <div>
          <p>
            I've learned that the best products come from understanding real
            problems. Here's what I bring:
          </p>
          <ul>
            <li>
              <span className="highlight">Listen-first approach</span>-
              SimplyLocal rating was 4.9 because I listen to understand.
            </li>
            <li>
              <span className="highlight">Relationship building</span>- My best
              partnerships came from genuine long term conversations
            </li>
            <li>
              <span className="highlight">Technical understanding</span> - I can
              (ELI5) explain complex topics simply
            </li>
            <li>
              <span className="highlight">Pattern recognition</span> Deeply
              empathetic, an ability to work real-time with non verbal comms.
            </li>
    
          </ul>
          <p>
            Be it software or sales, I'm a hyper committed individual who loves pushing himself. 
          </p>
        </div>
      ),
    },
  ];

  return (
    <TimelineContainer>
      <Toaster position="top-center" />

      <Header variants={containerVariants} initial="hidden" animate="visible">
        <ProfileImage
          variants={headerVariants}
          whileHover={{ scale: 1.05, rotate: 5 }}
          transition={{ duration: 0.3 }}
        >
          <img src={headImage} alt="Michael" />
        </ProfileImage>
        <NameContainer variants={headerVariants}>
          <Name>Michael Tibebu</Name>
          <SocialLinks>
            <a
              href="https://github.com/yilakt"
              target="_blank"
              rel="noopener noreferrer"
              className="github"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
              </svg>
            </a>
            <a
              href="/resume.pdf"
              download="Michael_Tibebu_Resume.pdf"
              className="resume"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            </a>
            {/* <a
              href="https://x.com/yilaktibebu"
              target="_blank"
              rel="noopener noreferrer"
              className="twitter"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a> */}
            <button
              onClick={handleEmailClick}
              className="email"
              style={{
                border: "none",
                background: "var(--secondary-bg)",
                cursor: "pointer",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
            </button>
          </SocialLinks>
        </NameContainer>
        <Title variants={headerVariants}>
          Senior Solutions Architect
        </Title>
        <TechStack
          variants={headerVariants}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <span className="tech">React Native - 7yrs</span>
          <span className="tech">React - 4yrs</span>
          <span className="tech">Node / Express - 7yrs</span>
          {/* <span className="tech">Tensorflow Lite - 4yrs</span> */}
          <span className="tech">LLMs - 3yrs</span>
          <span className="tech">AI Video Gen - 1yr</span>
        </TechStack>
        {/* <Subtitle variants={headerVariants}>
          A timeline of big events. Based in Colorado.
        </Subtitle> */}

        {emailCopied && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              textAlign: "center",
              fontSize: "0.9rem",
              color: "var(--text-secondary)",
              marginTop: "1rem",
            }}
          >
            ymtibebu@gmail.com • Copied!
          </motion.div>
        )}
      </Header>

      <BioSection
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <BioContent>
          Started as a junior developer in 2013, now a Software Solutions Architect bridging technical execution and business outcomes. Delivered high-impact apps for major brands like Popeyes, Payless, and Onyx (1m+ installs), plus AI government contracts. Built and sold SimplyLocal (300k users, acquired 2025). Passionate learner with deep expertise in emerging tech: <span className="highlight">AI/ML (local LLMs, video/image generation via HuggingFace)</span>, early Ethereum/Solidity development. Love mountain hiking with my dogs, volunteering at my local dog shelter, and architecting simple solutions for difficult problems.
        </BioContent>
      </BioSection>

      {/* <AccordionContainer
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        {accordionData.map((item, index) => (
          <AccordionItem
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
          >
            <AccordionHeader
              isOpen={openAccordion === index}
              onClick={() => toggleAccordion(index)}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <span>{item.title}</span>
            </AccordionHeader>
            <motion.div
              initial={false}
              animate={{
                height: openAccordion === index ? "auto" : 0,
                opacity: openAccordion === index ? 1 : 0,
              }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              style={{ overflow: "hidden" }}
            >
              <AccordionContent>{item.content}</AccordionContent>
            </motion.div>
          </AccordionItem>
        ))}
      </AccordionContainer> */}

      <TimelineLine
        accordionOpen={openAccordion !== null}
        variants={lineVariants}
        initial="hidden"
        animate="visible"
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ marginTop: '4rem' }}
      >
        {filteredData.map((item, index) => (
          <TimelineItem key={item.id} item={item} index={index} />
        ))}
      </motion.div>
    </TimelineContainer>
  );
};

export default Timeline;
